import {ViewController} from "data/types/structure";
import {inject, injectable} from "inversify";
import {makeAutoObservable, observable} from "mobx";
import {ModalType, RequestState, TeamRegion} from "data/enums";
import {Bindings} from "data/constants/bindings";
import type {IUserStore} from "data/stores/user/user.store";
import type {ILocalizationStore} from "data/stores/localization/localization.store";
import type {IModalsStore} from "data/stores/modals/modals.store";
import type {ITeamStore} from "data/stores/team/team.store";
import {compact, eq, size} from "lodash";
import type {IShareStore} from "data/stores/share/share.store";

export interface IGameBarController extends ViewController {
	get i18n(): ILocalizationStore;
	get currentLineup(): number[];
	get isLastStep(): boolean;
	get isCurrentLineupFull(): boolean;
	get isCurrentLineupEmpty(): boolean;
	get isTeamsSaved(): boolean;
	get isFinalSaved(): boolean;
	get currentStep(): number;

	toNextStep: () => void;
	onClear: () => void;
	onEditTeam: () => void;
	onFinalSaveTeam: () => void;
	onShareTeam: () => void;
	onPrevStep: () => void;
	onNewPick: () => void;
}

@injectable()
export class GameBarController implements IGameBarController {
	@observable _requestState: RequestState = RequestState.IDLE;

	get i18n() {
		return this._i18nStore;
	}

	get isLastStep() {
		return eq(this._teamStore.currentStep + 1, this._teamStore.stepSize);
	}

	get currentLineup() {
		return this._teamStore.currentLineup;
	}

	get currentStep() {
		return this._teamStore.currentStep;
	}

	get isCurrentLineupFull() {
		return this._teamStore.isCurrentLineupFull;
	}

	get isCurrentLineupEmpty() {
		return this._teamStore.isCurrentLineupEmpty;
	}

	get isTeamsSaved() {
		return this._teamStore.isSelectTeamCompleted;
	}

	get isFinalSaved() {
		return this._teamStore.isFinalSaved;
	}

	constructor(
		@inject(Bindings.UserStore) private _userStore: IUserStore,
		@inject(Bindings.LocalizationStore) private _i18nStore: ILocalizationStore,
		@inject(Bindings.ModalsStore) private _modalStore: IModalsStore,
		@inject(Bindings.TeamStore) private _teamStore: ITeamStore,
		@inject(Bindings.ShareStore) private _shareStore: IShareStore
	) {
		makeAutoObservable(this);
	}

	toNextStep = () => {
		this._teamStore.setNextStep();
	};

	onClear = () => {
		this._teamStore.clearPartLineupByStep();
	};

	onFinalSaveTeam = () => {
		const type = this._shareStore.getShareType;
		if (this._userStore.isAuthorized) {
			const isFullEuLineup = size(compact(this._teamStore.euLineup)) === 12;
			const isFullUsLineup = size(compact(this._teamStore.usLineup)) === 12;
			this._teamStore.clearIsPromptActive();
			this._teamStore.clearIsFinalSaved();

			if (isFullEuLineup) {
				void this._teamStore.onCreateTeam(TeamRegion.Europe);
			}

			if (isFullUsLineup) {
				void this._teamStore.onCreateTeam(TeamRegion.Usa);
			}

			void this._shareStore.shareTeam({lang: this._i18nStore.lang, type: type});
			this._shareStore.toggleShareModal();
		} else {
			this._modalStore.showModal(ModalType.LOSE_PICKS, {
				message: "",
			});
		}
	};

	onEditTeam = () => {
		this._teamStore.toFirstStep();
		this._teamStore.clearIsFinalSaved();
		this._teamStore.setSelectedRegion(this._teamStore.firstSelectedRegion);
		this._teamStore.clearIsTeamSkipped();
		this._teamStore.clearIsTeamsSaved();
	};

	onShareTeam = () => {
		const type = this._shareStore.getShareType;
		void this._shareStore.shareTeam({lang: this._i18nStore.lang, type: type});
		this._shareStore.toggleShareModal();
	};

	onPrevStep = () => {
		this._teamStore.setPrevStep();
	};

	onNewPick = () => {
		this._modalStore.showModal(ModalType.NEW_PICK);
	};

	dispose() {
		return;
	}

	init(param: void) {
		return;
	}
}
